<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  editing: { type: Object, default: null },
  suggestions: {
    type: Object,
    default: () => ({ campaigns: [], sources: [], mediums: [] }),
  },
});

const emit = defineEmits(['save', 'close']);

const { t } = useI18n();

const PLATFORMS = [
  { id: 'google_ads', label: 'Google Ads' },
  { id: 'meta_ads', label: 'Meta Ads' },
  { id: 'tiktok_ads', label: 'TikTok Ads' },
  { id: 'linkedin_ads', label: 'LinkedIn Ads' },
  { id: 'twitter_ads', label: 'Twitter/X Ads' },
  { id: 'pinterest_ads', label: 'Pinterest Ads' },
  { id: 'email', label: 'Email' },
  { id: 'broadcast', label: 'Broadcast' },
  { id: 'evento', label: t('CRM_REPORTS.MARKETING_COST_MODAL.PLATFORM_EVENT') },
  { id: 'offline', label: 'Offline' },
  { id: 'outro', label: t('CRM_REPORTS.MARKETING_COST_MODAL.PLATFORM_OTHER') },
];

const CATEGORIES = [
  { id: 'awareness', label: 'Awareness' },
  {
    id: 'consideration',
    label: t('CRM_REPORTS.MARKETING_COST_MODAL.CATEGORY_CONSIDERATION'),
  },
  {
    id: 'conversion',
    label: t('CRM_REPORTS.MARKETING_COST_MODAL.CATEGORY_CONVERSION'),
  },
  {
    id: 'retention',
    label: t('CRM_REPORTS.MARKETING_COST_MODAL.CATEGORY_RETENTION'),
  },
];

const CURRENCIES = [
  { id: 'BRL', label: 'R$ (BRL)' },
  { id: 'USD', label: '$ (USD)' },
  { id: 'EUR', label: '€ (EUR)' },
];

// Plataformas coletadas automaticamente (gasto real via API) — custo manual delas
// é ignorado no relatório (o real vence). Mantém sincronizado com a coleta.
const COLLECTED_PLATFORMS = ['google_ads', 'meta_ads'];

// Form state
const form = ref({
  name: '',
  platform: '',
  utm_campaign: '',
  utm_source: '',
  utm_medium: '',
  cost: '',
  currency: 'BRL',
  exchange_rate: '',
  start_date: '',
  end_date: '',
  category: '',
  notes: '',
});

// Populate form if editing
watch(
  () => props.editing,
  val => {
    if (val) {
      form.value = {
        name: val.name || '',
        platform: val.platform || '',
        utm_campaign: val.utm_campaign || '',
        utm_source: val.utm_source || '',
        utm_medium: val.utm_medium || '',
        cost: val.cost || '',
        currency: val.currency || 'BRL',
        exchange_rate: val.exchange_rate || '',
        start_date: val.start_date || '',
        end_date: val.end_date || '',
        category: val.category || '',
        notes: val.notes || '',
      };
    }
  },
  { immediate: true }
);

const isEditing = computed(() => !!props.editing);
const title = computed(() =>
  isEditing.value
    ? t('CRM_REPORTS.MARKETING_COST_MODAL.TITLE_EDIT')
    : t('CRM_REPORTS.MARKETING_COST_MODAL.TITLE_NEW')
);

// Autocomplete state
const campaignQuery = ref('');
const sourceQuery = ref('');
const mediumQuery = ref('');
const showCampaignDropdown = ref(false);
const showSourceDropdown = ref(false);
const showMediumDropdown = ref(false);

const filteredCampaigns = computed(() => {
  const q = (
    campaignQuery.value ||
    form.value.utm_campaign ||
    ''
  ).toLowerCase();
  if (!q) return props.suggestions.campaigns.slice(0, 15);
  return props.suggestions.campaigns
    .filter(c => c.toLowerCase().includes(q))
    .slice(0, 15);
});

const filteredSources = computed(() => {
  const q = (sourceQuery.value || form.value.utm_source || '').toLowerCase();
  if (!q) return props.suggestions.sources.slice(0, 10);
  return props.suggestions.sources
    .filter(s => s.toLowerCase().includes(q))
    .slice(0, 10);
});

const filteredMediums = computed(() => {
  const q = (mediumQuery.value || form.value.utm_medium || '').toLowerCase();
  if (!q) return props.suggestions.mediums.slice(0, 10);
  return props.suggestions.mediums
    .filter(m => m.toLowerCase().includes(q))
    .slice(0, 10);
});

const selectCampaign = val => {
  form.value.utm_campaign = val;
  campaignQuery.value = '';
  showCampaignDropdown.value = false;
};
const selectSource = val => {
  form.value.utm_source = val;
  sourceQuery.value = '';
  showSourceDropdown.value = false;
};
const selectMedium = val => {
  form.value.utm_medium = val;
  mediumQuery.value = '';
  showMediumDropdown.value = false;
};

const isAutoCollected = computed(() =>
  COLLECTED_PLATFORMS.includes(form.value.platform)
);

// Moeda estrangeira: exige cotação p/ congelar o valor em BRL (convert-at-entry).
const isForeign = computed(() => form.value.currency !== 'BRL');

const costBrlPreview = computed(() => {
  const cost = Number(form.value.cost) || 0;
  const rate = Number(form.value.exchange_rate) || 0;
  if (!cost || !rate) return 0;
  return Math.round(cost * rate * 100) / 100;
});

const isValid = computed(
  () =>
    form.value.name.trim() &&
    Number(form.value.cost) >= 0 &&
    (!isForeign.value || Number(form.value.exchange_rate) > 0)
);

const onSubmit = () => {
  if (!isValid.value) return;
  emit('save', {
    marketing_cost: {
      ...form.value,
      cost: Number(form.value.cost) || 0,
      exchange_rate: isForeign.value
        ? Number(form.value.exchange_rate) || 0
        : 1,
    },
  });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-solid-1 rounded-xl border border-n-weak shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-3 border-b border-n-weak"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ title }}
        </h3>
        <button
          class="text-n-slate-10 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <!-- Form -->
      <form class="p-5 space-y-4" @submit.prevent="onSubmit">
        <!-- Name -->
        <div>
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.NAME_LABEL') }}
          </label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500 focus:border-woot-500"
            :placeholder="
              $t('CRM_REPORTS.MARKETING_COST_MODAL.NAME_PLACEHOLDER')
            "
          />
        </div>

        <!-- Platform -->
        <div>
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.PLATFORM_LABEL') }}
          </label>
          <select
            v-model="form.platform"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
          >
            <option value="">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.SELECT_PLACEHOLDER') }}
            </option>
            <option v-for="p in PLATFORMS" :key="p.id" :value="p.id">
              {{ p.label }}
            </option>
          </select>
        </div>

        <!-- Anti-duplication warning for auto-collected platforms -->
        <div
          v-if="isAutoCollected"
          class="flex items-start gap-2 p-3 text-xs rounded-lg bg-n-amber-3 text-n-amber-11"
        >
          <span class="i-lucide-info size-4 shrink-0 mt-0.5" />
          <span>
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.AUTO_COLLECTED_WARNING') }}
          </span>
        </div>

        <!-- UTM Campaign (autocomplete) -->
        <div class="relative">
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_CAMPAIGN_LABEL') }}
            <span class="text-n-slate-8 font-normal">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_CAMPAIGN_HINT') }}
            </span>
          </label>
          <input
            v-model="form.utm_campaign"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
            :placeholder="
              $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_CAMPAIGN_PLACEHOLDER')
            "
            @focus="showCampaignDropdown = true"
            @input="campaignQuery = form.utm_campaign"
            @blur="setTimeout(() => (showCampaignDropdown = false), 200)"
          />
          <div
            v-if="showCampaignDropdown && filteredCampaigns.length"
            class="absolute z-10 mt-1 w-full bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
          >
            <button
              v-for="c in filteredCampaigns"
              :key="c"
              type="button"
              class="w-full text-left px-3 py-2 text-xs text-n-slate-11 hover:bg-woot-500/10 hover:text-woot-600"
              @mousedown.prevent="selectCampaign(c)"
            >
              {{ c }}
            </button>
          </div>
        </div>

        <!-- UTM Source + Medium (autocomplete, side by side) -->
        <div class="grid grid-cols-2 gap-3">
          <div class="relative">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_SOURCE_LABEL') }}
            </label>
            <input
              v-model="form.utm_source"
              type="text"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
              :placeholder="
                $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_SOURCE_PLACEHOLDER')
              "
              @focus="showSourceDropdown = true"
              @input="sourceQuery = form.utm_source"
              @blur="setTimeout(() => (showSourceDropdown = false), 200)"
            />
            <div
              v-if="showSourceDropdown && filteredSources.length"
              class="absolute z-10 mt-1 w-full bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-36 overflow-y-auto"
            >
              <button
                v-for="s in filteredSources"
                :key="s"
                type="button"
                class="w-full text-left px-3 py-2 text-xs text-n-slate-11 hover:bg-woot-500/10 hover:text-woot-600"
                @mousedown.prevent="selectSource(s)"
              >
                {{ s }}
              </button>
            </div>
          </div>

          <div class="relative">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_MEDIUM_LABEL') }}
            </label>
            <input
              v-model="form.utm_medium"
              type="text"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
              :placeholder="
                $t('CRM_REPORTS.MARKETING_COST_MODAL.UTM_MEDIUM_PLACEHOLDER')
              "
              @focus="showMediumDropdown = true"
              @input="mediumQuery = form.utm_medium"
              @blur="setTimeout(() => (showMediumDropdown = false), 200)"
            />
            <div
              v-if="showMediumDropdown && filteredMediums.length"
              class="absolute z-10 mt-1 w-full bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-36 overflow-y-auto"
            >
              <button
                v-for="m in filteredMediums"
                :key="m"
                type="button"
                class="w-full text-left px-3 py-2 text-xs text-n-slate-11 hover:bg-woot-500/10 hover:text-woot-600"
                @mousedown.prevent="selectMedium(m)"
              >
                {{ m }}
              </button>
            </div>
          </div>
        </div>

        <!-- Cost + Currency -->
        <div class="grid grid-cols-3 gap-3">
          <div class="col-span-2">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.COST_LABEL') }}
            </label>
            <input
              v-model="form.cost"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.CURRENCY_LABEL') }}
            </label>
            <select
              v-model="form.currency"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
            >
              <option v-for="cur in CURRENCIES" :key="cur.id" :value="cur.id">
                {{ cur.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Exchange rate (moeda estrangeira → congela BRL no cadastro) -->
        <div
          v-if="isForeign"
          class="grid grid-cols-3 gap-3 p-3 rounded-lg bg-n-alpha-black2"
        >
          <div class="col-span-2">
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.EXCHANGE_RATE_LABEL') }}
              <span class="text-n-slate-8 font-normal">
                {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.EXCHANGE_RATE_HINT') }}
              </span>
            </label>
            <input
              v-model="form.exchange_rate"
              type="number"
              step="0.0001"
              min="0"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
              :placeholder="
                $t('CRM_REPORTS.MARKETING_COST_MODAL.EXCHANGE_RATE_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.EQUIVALENT_LABEL') }}
            </label>
            <p class="px-1 py-2 text-sm font-medium text-n-teal-11">
              R$ {{ costBrlPreview.toFixed(2) }}
            </p>
          </div>
          <p class="col-span-3 text-[11px] text-n-slate-9">
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.EXCHANGE_RATE_NOTE') }}
          </p>
        </div>

        <!-- Dates -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.START_DATE_LABEL') }}
            </label>
            <input
              v-model="form.start_date"
              type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-10 mb-1">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.END_DATE_LABEL') }}
            </label>
            <input
              v-model="form.end_date"
              type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
            />
          </div>
        </div>

        <!-- Category -->
        <div>
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.CATEGORY_LABEL') }}
          </label>
          <select
            v-model="form.category"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12"
          >
            <option value="">
              {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.SELECT_PLACEHOLDER') }}
            </option>
            <option v-for="cat in CATEGORIES" :key="cat.id" :value="cat.id">
              {{ cat.label }}
            </option>
          </select>
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.NOTES_LABEL') }}
          </label>
          <textarea
            v-model="form.notes"
            rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-woot-500 resize-none"
            :placeholder="
              $t('CRM_REPORTS.MARKETING_COST_MODAL.NOTES_PLACEHOLDER')
            "
          />
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            class="px-4 py-2 text-xs font-medium rounded-lg text-n-slate-11 hover:bg-n-alpha-1/50 transition-colors"
            @click="emit('close')"
          >
            {{ $t('CRM_REPORTS.MARKETING_COST_MODAL.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="!isValid"
            class="px-4 py-2 text-xs font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{
              isEditing
                ? $t('CRM_REPORTS.MARKETING_COST_MODAL.SAVE')
                : $t('CRM_REPORTS.MARKETING_COST_MODAL.CREATE')
            }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
